// The floating formula of one product line and one side, shown under that
// side's Fixed | Floating toggle (ledger row 132, AC14).
//
// Until now picking "Floating" captured the word and nothing else: nobody
// could say floating on WHAT, or work the price out. When the side floats,
// this line shows the formula in the desk's own shorthand ("AMFSA00 Close ·
// September 2026 WMA + USD 8.00/MT"), where its price stands, and the button
// that opens the formula window. A fixed or unanswered side shows nothing
// here: its price box above is the whole story, exactly as before.

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faRotateRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { canonicalizeAmount } from '../../../utils/amountFormat';
import FloatingFormulaModal from './FloatingFormulaModal';
import { useFloatingFormulaContext } from './FloatingFormulaContext';
import { findFormulaLine, PRICING_TYPE_FIELD } from './floatingFormulaGate';
import {
  formatTypedPrice,
  lineStatusText,
  priceText,
  quantityText,
  resultHasPrice,
  samePrice,
} from './floatingFormulaText';
import './FloatingFormula.css';

export const NO_FORMULA_TEXT = 'No formula yet — the hedging desk sets it';
export const SAVE_FIRST_TEXT = 'The formula can be added once the enquiry is saved.';

const LINE_TITLE = {
  sell: 'Floating sales price formula',
  buy: 'Floating purchase price formula',
};

// Row 138 (Chris 2026-09-21, "this is Phils area"): only the hedging desk sets
// a floating formula. The server answers per side (``can_edit``); anyone else
// sees the formula and its price, with no edit button.
export const CANNOT_EDIT_TEXT = 'The hedging desk sets this formula.';

const TYPED_PRICE = {
  sell: { field: 'sell_price', label: 'Sell Price' },
  buy: { field: 'buy_price', label: 'Buy Price' },
};

/**
 * @param {'sell'|'buy'} side
 * @param {object} row        the form's product line (Product 1 = the inline
 *                            fields, Product 2.. = an ``extra_products`` row)
 * @param {number} lineNumber 1 for Product 1, 2.. for the rest
 */
const FloatingFormulaLine = ({ side, row, lineNumber }) => {
  const ctx = useFloatingFormulaContext();
  const [editing, setEditing] = useState(false);

  if (!ctx || !row) return null;
  if (row[PRICING_TYPE_FIELD[side]] !== 'floating') return null;
  // Belt and braces: the Supplier Pricing section is already hidden from a
  // customer trader and the server never sends them the purchase side.
  if (side === 'buy' && ctx.isCustomerTrader) return null;

  const isProductOne = lineNumber === 1;
  const productId = isProductOne ? ctx.productOneId : (row.id ?? null);
  const saved = isProductOne ? ctx.enquiryId != null : row.id != null;
  const formulaLine = saved && ctx.status === 'ready' ? findFormulaLine(ctx.data, productId) : null;
  const lineSide = formulaLine ? formulaLine[side] : null;
  const formula = lineSide?.formula || null;
  const result = lineSide?.result || null;
  const canEdit = Boolean(ctx.data?.can_edit?.[side]);

  let body;
  let blockedReason = null;
  let showBlockedReason = false;
  if (!saved) {
    body = <div className="ff-line-muted">{SAVE_FIRST_TEXT}</div>;
    blockedReason = SAVE_FIRST_TEXT;
  } else if (ctx.status === 'failed') {
    body = (
      <div className="ff-line-problem" role="alert">
        The floating formula could not be loaded: {ctx.error}{' '}
        <button type="button" className="ff-link-btn" onClick={() => ctx.reload()}>
          <FontAwesomeIcon icon={faRotateRight} /> Try again
        </button>
      </div>
    );
    blockedReason = 'The floating formula could not be loaded.';
  } else if (ctx.status !== 'ready') {
    body = (
      <div className="ff-line-muted">
        <FontAwesomeIcon icon={faSpinner} spin /> Loading the floating formula…
      </div>
    );
    blockedReason = 'Loading the floating formula…';
  } else if (!formulaLine) {
    // A row saved moments ago whose list has not been read again yet.
    body = <div className="ff-line-muted">{SAVE_FIRST_TEXT}</div>;
    blockedReason = SAVE_FIRST_TEXT;
  } else {
    if (!formula) {
      // Red only for the desk, whose to-do it is; a trader has nothing to do here.
      body = <div className={canEdit ? 'ff-line-missing' : 'ff-line-muted'}>{NO_FORMULA_TEXT}</div>;
    } else {
      const text = result?.formula_text || formula.snapshot?.formula_text || '';
      // AC36c: the formula never overwrites the typed price in v1. When the
      // two disagree the line says so, instead of leaving the trader to
      // notice two different numbers.
      const typed = row[TYPED_PRICE[side].field];
      const differs = resultHasPrice(result)
        && canonicalizeAmount(typed) !== ''
        && !samePrice(typed, result.final_price);
      body = (
        <>
          {text && <div className="ff-line-formula">{text}</div>}
          {result && (
            <div className={`ff-line-status ff-line-status--${result.status}`}>
              {lineStatusText(result)}
            </div>
          )}
          {differs && (
            <div className="ff-line-note">
              The {TYPED_PRICE[side].label} typed above is {result.currency}{' '}
              {formatTypedPrice(typed)}, not the formula&rsquo;s {priceText(result)}.
              The formula never changes the typed price.
            </div>
          )}
        </>
      );
    }
    if (!canEdit) {
      blockedReason = CANNOT_EDIT_TEXT;
      // The empty line already says who sets it.
      showBlockedReason = Boolean(formula);
    }
  }

  const closeAndReload = () => {
    setEditing(false);
    ctx.reload();
  };

  return (
    <div className={`ff-line ff-line--${side}`} data-testid={`ff-line-${side}-${lineNumber}`}>
      <div className="ff-line-body">
        <div className="ff-line-title">{LINE_TITLE[side]}</div>
        {body}
      </div>
      <div className="ff-line-actions">
        {canEdit && (
          <button
            type="button"
            className="fef-btn fef-btn-secondary ff-line-edit"
            onClick={() => setEditing(true)}
            disabled={Boolean(blockedReason)}
            title={blockedReason || undefined}
          >
            <FontAwesomeIcon icon={faPen} /> Edit floating formula
          </button>
        )}
        {showBlockedReason && <div className="ff-line-reason">{blockedReason}</div>}
      </div>

      {/* Only while the line is known: a reload that fails under an open
          window must close it, not crash on a line that is no longer there. */}
      {editing && formulaLine && (
        <FloatingFormulaModal
          enquiryId={ctx.enquiryId}
          displayId={ctx.displayId}
          side={side}
          productId={productId}
          lineLabel={`Product ${lineNumber}${formulaLine.label ? ` · ${formulaLine.label}` : ''}`}
          quantity={quantityText(row.min_tonnage, row.max_tonnage)}
          dealUnit={formulaLine.unit_code}
          formula={formula}
          initialResult={result}
          canEdit={canEdit}
          onClose={() => setEditing(false)}
          onSaved={closeAndReload}
          onRemoved={closeAndReload}
        />
      )}
    </div>
  );
};

export default FloatingFormulaLine;
